import { useCallback } from 'react';
import {
  ButtonProps,
  makeStyles,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import { processEvents } from '@utils/customEvent';
import useProjectDetails from '@hooks/useProjectDetails';
import { CustomizedButton, Stack } from '@components/Customized';
import {
  CancelRequestModal,
  RefundRequestModal,
  PaymentApprovalModal
} from '@routes/Payment/component';
import { CapaPurchaseOrderStatus } from '@generated/graphql';
import { getPriceDatas } from '../CreatePurchaseOrder/utils/getTotalAmount';
import useQueryString from '@hooks/useQueryString';

interface BottomButtonGroupProps {
  rfqId?: string;
  quotationPartnerId?: string;
  isClosedConsulting?: boolean;
  chatRoomId?: string;
}

const refundRequestedStatus = [
  CapaPurchaseOrderStatus.RefundRequested,
  CapaPurchaseOrderStatus.CancelRequested
];

const BottomButtonGroup: React.FunctionComponent<BottomButtonGroupProps> = ({
  rfqId,
  quotationPartnerId,
  isClosedConsulting,
  chatRoomId
}) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('md'));
  const { cta } = useStyles();
  const { chatroom, partnerInfo, purchaseOrders, rfqInfo } = useProjectDetails({
    chatRoomId,
    watches: ['rfqInfo', 'purchaseOrders', 'partnerInfo']
  });
  const currentPurchaseOrder = (purchaseOrders ?? [])?.[0];
  const orderStatus = currentPurchaseOrder?.status;
  const isRefundRequested = refundRequestedStatus.includes(orderStatus);
  const hasReview = chatroom?.hasReview ?? false;
  const { totalAmount } = getPriceDatas(currentPurchaseOrder);

  const openReviewModal = useCallback(() => {
    processEvents.리뷰작성({
      rfqId: rfqId ?? '',
      quotationPartnerId: quotationPartnerId ?? '',
      partnerProfileLogoId: partnerInfo?.logo?.fileId ?? '',
      projectName: rfqInfo?.projectName ?? '',
      organizationName: partnerInfo?.organizationName ?? ''
    });
  }, [
    rfqId,
    quotationPartnerId,
    partnerInfo?.logo?.fileId,
    rfqInfo?.projectName,
    partnerInfo?.organizationName
  ]);

  const commonButtonProps: Partial<ButtonProps> = {
    className: cta,
    fullWidth: true,
    size: 'large',
    disabled: isClosedConsulting || isRefundRequested || hasReview
  };

  return (
    <Stack width="100%" justifyContent="flex-end" gap={!matches ? 12 : 8}>
      <ButtonByStatus
        buttonProps={commonButtonProps}
        orderStatus={orderStatus}
        chatRoomId={chatRoomId}
        openReviewModal={openReviewModal}
        purchaseOrderId={currentPurchaseOrder?.id}
        quotationPartnerId={quotationPartnerId}
        rfqId={rfqId}
        totalAmount={totalAmount}
      />
    </Stack>
  );
};

export default BottomButtonGroup;

const useStyles = makeStyles((theme) => ({
  cta: {
    maxWidth: 144,
    [theme.breakpoints.down('md')]: {
      maxWidth: 'unset'
    }
  }
}));

interface ButtonByStatusProps {
  buttonProps: Partial<ButtonProps>;
  orderStatus?: CapaPurchaseOrderStatus;
  chatRoomId?: string;
  quotationPartnerId?: string;
  rfqId?: string;
  purchaseOrderId?: string;
  totalAmount?: number;
  registerExpectedDate?: string;
  isExtended?: boolean;
  openReviewModal?: () => void;
}

export const ButtonByStatus = ({
  buttonProps,
  orderStatus: optionalOrderStatus,
  chatRoomId: optionalChatRoomId,
  quotationPartnerId: optionalQuotationPartnerId,
  rfqId: optionalRfqId,
  purchaseOrderId: optionalPurchaseOrderId,
  totalAmount: optionalTotalAmount,
  openReviewModal
}: ButtonByStatusProps) => {
  const purchaseOrderId = optionalPurchaseOrderId ?? '';
  const quotationPartnerId = optionalQuotationPartnerId ?? '';
  const rfqId = optionalRfqId ?? '';
  const chatRoomId = optionalChatRoomId ?? '';
  const totalAmount = optionalTotalAmount ?? 0;
  const orderStatus = optionalOrderStatus ?? '';
  const { query, onReplaceQueryString } = useQueryString<any>();

  switch (orderStatus) {
    /** 결제 기능 추가 전 발행된 발주서 */
    case CapaPurchaseOrderStatus.Created: {
      return (
        <CustomizedButton
          {...buttonProps}
          color="dark"
          onClick={() =>
            processEvents.발주서재발행({
              chatRoomId
            })
          }
        >
          발주서 재발행
        </CustomizedButton>
      );
    }
    // 가상 계좌 결제 전
    case CapaPurchaseOrderStatus.ProcessPayment: {
      return (
        <>
          <CustomizedButton
            {...buttonProps}
            color="default"
            disabled={false}
            onClick={() =>
              processEvents.채팅으로이동({
                chatRoomId
              })
            }
          >
            채팅으로 이동
          </CustomizedButton>

          <CancelRequestModal
            quotationPartnerId={quotationPartnerId}
            rfqId={rfqId}
            purchaseOrderId={purchaseOrderId}
            amount={totalAmount}
            openButton={(onClick) => (
              <CustomizedButton
                {...buttonProps}
                color="primary"
                onClick={onClick}
              >
                결제취소
              </CustomizedButton>
            )}
          />
        </>
      );
    }

    /** 결제 후 제조시작 전 */
    case CapaPurchaseOrderStatus.Paid:
    case CapaPurchaseOrderStatus.CancelRequested: {
      return (
        <>
          <CustomizedButton
            {...buttonProps}
            color="default"
            disabled={false}
            onClick={() =>
              processEvents.채팅으로이동({
                chatRoomId
              })
            }
          >
            채팅으로 이동
          </CustomizedButton>
          <CancelRequestModal
            quotationPartnerId={quotationPartnerId}
            rfqId={rfqId}
            purchaseOrderId={purchaseOrderId}
            amount={totalAmount}
            openButton={(onClick) => (
              <CustomizedButton
                {...buttonProps}
                color="primary"
                onClick={onClick}
              >
                결제취소
              </CustomizedButton>
            )}
          />
        </>
      );
    }

    /** 제조시작 & 환불대기 */
    case CapaPurchaseOrderStatus.RefundRequested:
    case CapaPurchaseOrderStatus.Processing: {
      return (
        <>
          <CustomizedButton
            {...buttonProps}
            color="default"
            disabled={false}
            onClick={() =>
              processEvents.채팅으로이동({
                chatRoomId
              })
            }
          >
            채팅으로 이동
          </CustomizedButton>
          <RefundRequestModal
            purchaseOrderId={purchaseOrderId}
            amount={totalAmount}
            quotationPartnerId={quotationPartnerId}
            rfqId={rfqId}
            openButton={(onClick) => (
              <CustomizedButton
                {...buttonProps}
                color="primary"
                onClick={onClick}
              >
                환불요청
              </CustomizedButton>
            )}
          />
        </>
      );
    }

    /** 환불 완료 */
    case CapaPurchaseOrderStatus.RefundConfirmed: {
      return (
        <>
          <CustomizedButton
            {...buttonProps}
            color="default"
            disabled={false}
            onClick={() =>
              processEvents.채팅으로이동({
                chatRoomId
              })
            }
          >
            채팅으로 이동
          </CustomizedButton>
          <CustomizedButton
            {...buttonProps}
            color="dark"
            onClick={() =>
              processEvents.발주서재발행({
                chatRoomId
              })
            }
          >
            발주서 재발행
          </CustomizedButton>
        </>
      );
    }
    /** 배송시작 */
    case CapaPurchaseOrderStatus.Shipping: {
      return (
        <>
          <CustomizedButton
            {...buttonProps}
            color="default"
            disabled={false}
            onClick={() =>
              processEvents.채팅으로이동({
                chatRoomId
              })
            }
          >
            채팅으로 이동
          </CustomizedButton>
          <PaymentApprovalModal
            quotationPartnerId={quotationPartnerId}
            rfqId={rfqId}
            openButton={(onClick) => (
              <CustomizedButton
                {...buttonProps}
                color="primary"
                onClick={(props) => {
                  onReplaceQueryString({
                    ...(query as any),
                    p: '6'
                  });
                  onClick(props);
                }}
              >
                <i className="request-gtm-confirm">구매확정</i>
              </CustomizedButton>
            )}
          />
        </>
      );
    }

    /** 거래완료 */
    case CapaPurchaseOrderStatus.PurchaseConfirmed: {
      return (
        <>
          <CustomizedButton
            {...buttonProps}
            disabled={false}
            color="default"
            onClick={() =>
              processEvents.채팅으로이동({
                chatRoomId
              })
            }
          >
            채팅으로 이동
          </CustomizedButton>
          <CustomizedButton
            {...buttonProps}
            color="primary"
            onClick={openReviewModal}
          >
            리뷰작성
          </CustomizedButton>
        </>
      );
    }

    /** 거래중단 */
    case CapaPurchaseOrderStatus.NotPaidCanceled:
    case CapaPurchaseOrderStatus.CancelConfirmed:
    case CapaPurchaseOrderStatus.RejectOrderByPartner:
    case CapaPurchaseOrderStatus.CancelledByAdmin: {
      return (
        <>
          <CustomizedButton
            {...buttonProps}
            color="default"
            disabled={false}
            onClick={() =>
              processEvents.채팅으로이동({
                chatRoomId
              })
            }
          >
            채팅으로 이동
          </CustomizedButton>
          <CustomizedButton
            {...buttonProps}
            color="dark"
            onClick={() =>
              processEvents.발주서재발행({
                chatRoomId
              })
            }
          >
            발주서 재발행
          </CustomizedButton>
        </>
      );
    }

    default:
      return null;
  }
};
